<template>
  <v-list-item
    class="d-flex align-center"
    :class="{ 'account-cell--mobile-typography': $vuetify.breakpoint.smAndDown }"
    link
    @keydown="$emit('keydown-emit', $event)"
    @focus="$emit('focus', $event)"
    :to="{
      name: 'accounts-view',
      params: {
        id: account.objectID,
      },
    }"
  >
    <v-list-item-avatar
      tile
      color="background2"
      class="rounded-lg"
      style="
        box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px,
          rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
      "
      :style="{
        border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
      }"
      :size="$vuetify.breakpoint.smAndDown ? 44 : 50"
    >
      <v-progress-circular
        v-if="logoLoading"
        indeterminate
        size="15"
        width="2"
        color="grey darken-2"
      ></v-progress-circular>
      <v-icon
        v-else-if="!logo?.length"
        :size="$vuetify.breakpoint.smAndDown ? 22 : 25"
        >mdi-folder-account</v-icon
      >
      <v-img
        v-else
        contain
        :src="logo"
        :alt="`${$options.filters.capitalize(account.name)} Logo`"
      >
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular
              indeterminate
              size="15"
              width="2"
              color="grey darken-2"
            ></v-progress-circular>
          </v-row>
        </template>
      </v-img>
    </v-list-item-avatar>
    <v-list-item-content :class="$vuetify.breakpoint.smAndDown ? 'py-1' : 'py-2'">
      <v-list-item-title>
        <span
          :class="[
            'text-capitalize text-wrap font-weight-bold',
            $vuetify.breakpoint.smAndDown ? 'text-body-2' : '',
          ]"
          >{{ account.name }}</span
        >
      </v-list-item-title>
      <v-list-item-subtitle>
        <span
          :class="[
            'text-capitalize text-wrap font-weight-medium',
            $vuetify.breakpoint.smAndDown
              ? 'text-caption text--secondary'
              : 'text-body-2',
          ]"
          >{{ account.dba }}</span
        >
      </v-list-item-subtitle>
      <v-list-item-subtitle>
        <span
          :class="[
            'text-capitalize text-wrap',
            $vuetify.breakpoint.smAndDown ? 'text-caption text--secondary' : '',
          ]"
          >{{ account["location.shipping.street.number"] }}
          {{ account["location.shipping.street.name"] }},
          {{ account["location.shipping.city.name"] }},
          {{ account["location.shipping.state.name"] ?? "" }}
          {{ account["location.shipping.zipCode"] }}</span
        >
      </v-list-item-subtitle>
    </v-list-item-content>
    <v-list-item-action>
      <div class="d-flex align-center" style="gap: 8px">
        <!-- Desktop: status pill (no icon) -->
        <StatusChip
          v-if="showStatus && accountStatusDef && $vuetify.breakpoint.mdAndUp"
          no-top-margin
          :status="accountStatusDef"
          class="text-capitalize"
        />

         <!-- Quick actions: centered dialog (all breakpoints) -->
         <span v-if="showActions" @click.stop @mousedown.stop>
          <v-btn icon small @click="openActionsMenu" aria-label="Quick actions">
            <v-icon small>mdi-dots-horizontal</v-icon>
          </v-btn>

          <v-dialog
            v-model="actionsMenuOpen"
            max-width="520"
            width="92vw"
            scrollable
            content-class="rounded-lg"
            :key="`dialog-${account?.objectID}`"
          >
            <v-card class="rounded-lg">
              <v-card-title class="py-2 px-3 d-flex flex-column">
                <div class="d-flex align-center" style="width: 100%">
                  <span class="text-body-2 font-weight-bold">{{
                    account?.name || "Account"
                  }}</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    @click="actionsMenuOpen = false"
                    aria-label="Close"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div
                  class="d-flex align-center justify-space-between"
                  style="width: 100%; gap: 8px"
                >
                  <span class="text-caption text--secondary text-truncate">
                    {{ account?.dba || "-" }} |
                    <StatusChip
                      v-if="accountStatusDef"
                      no-top-margin
                      :status="accountStatusDef"
                      class="text-capitalize d-inline-flex align-center"
                    />
                  </span>
                </div>
                <div
                  class="text-caption text--secondary mt-1"
                  style="width: 100%"
                >
                  <span class="text-capitalize">
                    {{ account["location.shipping.street.number"] }}
                    {{ account["location.shipping.street.name"] }},
                    {{ account["location.shipping.city.name"] }},
                    {{ account["location.shipping.state.name"] ?? "" }}
                    {{ account["location.shipping.zipCode"] }}
                  </span>
                </div>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="py-2 px-2">
                <v-row dense class="ma-0">
                  <v-col cols="6" class="pa-0 pr-1">
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!defaultBrandId"
                      @click.stop.prevent="createNewOrder"
                      @mousedown.stop.prevent
                    >
                      <v-icon small left>mdi-clipboard-plus</v-icon>
                      <span class="text-caption font-weight-bold"
                        >New Order</span
                      >
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="pa-0 pl-1">
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!hasAvailablePhone(account)"
                      @click.stop.prevent="startCall(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon small left>mdi-phone</v-icon>
                      <span class="text-caption font-weight-bold">Call</span>
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="pa-0 pr-1 mt-1">
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!hasAvailableEmail(account)"
                      @click.stop.prevent="sendEmail(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon small left>mdi-email</v-icon>
                      <span class="text-caption font-weight-bold">Email</span>
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="pa-0 pl-1 mt-1">
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      @click.stop.prevent="openDirections(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon small left>mdi-map-marker</v-icon>
                      <span class="text-caption font-weight-bold"
                        >Directions</span
                      >
                    </v-btn>
                  </v-col>
                </v-row>

                <div
                  class="d-flex align-center justify-space-between mt-3 px-1"
                >
                  <span
                    class="text-caption font-weight-bold text--secondary text-no-wrap"
                  >
                    Contacts ({{ contacts.length }})
                  </span>
                  <v-btn
                    text
                    x-small
                    class="px-2"
                    @click.stop.prevent="toggleContacts"
                  >
                    <span
                      class="text-caption font-weight-bold text--secondary text-no-wrap"
                    >
                      {{ contactsExpanded ? "Hide" : "Show" }}
                    </span>
                    <v-icon
                      x-small
                      class="ml-1"
                      :style="`transform: rotate(${
                        contactsExpanded ? 180 : 0
                      }deg); transition: transform 0.15s ease;`"
                      >mdi-chevron-down</v-icon
                    >
                  </v-btn>
                </div>
              </v-card-text>

              <v-expand-transition>
                <div v-show="contactsExpanded">
                  <v-divider class="my-0"></v-divider>
                  <div style="max-height: 45vh; overflow-y: auto">
                    <v-list dense class="py-0">
                      <v-list-item
                        v-if="contactsLoading"
                        dense
                        class="py-0 px-2"
                      >
                        <v-list-item-content class="py-1">
                          <v-list-item-subtitle class="text--disabled"
                            >Loading contacts...</v-list-item-subtitle
                          >
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        v-else-if="contacts.length === 0"
                        dense
                        class="py-0 px-2"
                      >
                        <v-list-item-content class="py-1">
                          <v-list-item-subtitle class="text--disabled"
                            >No contacts found.</v-list-item-subtitle
                          >
                        </v-list-item-content>
                      </v-list-item>

                      <div
                        v-else
                        v-for="(contact, i) in contacts"
                        :key="`dialog-${contact.id}`"
                      >
                        <v-list-item
                          dense
                          class="py-0 px-2"
                          @click.stop="contact.expanded = !contact.expanded"
                        >
                          <v-list-item-content class="py-1">
                            <v-list-item-title
                              class="text-body-2 font-weight-medium text-capitalize text-no-wrap"
                            >
                              {{ contact.fullName }}
                              <span
                                v-if="contact.parent"
                                class="text-caption text--disabled"
                              >
                                (parent)
                              </span>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-icon
                              x-small
                              color="grey"
                              :style="`transform: rotate(${
                                contact.expanded ? 180 : 0
                              }deg); transition: transform 0.15s ease;`"
                            >
                              mdi-chevron-down
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>

                        <v-expand-transition>
                          <div v-show="contact.expanded" class="px-3 pb-2">
                            <v-row dense class="ma-0">
                              <v-col cols="6" class="pa-0 pr-1">
                                <v-btn
                                  small
                                  block
                                  depressed
                                  class="rounded-lg text-none justify-start px-2"
                                  :disabled="!hasAvailablePhone(contact)"
                                  @click.stop.prevent="startCall(contact)"
                                  @mousedown.stop.prevent
                                >
                                  <v-icon small left>mdi-phone</v-icon>
                                  <span class="text-caption font-weight-bold"
                                    >Call</span
                                  >
                                </v-btn>
                              </v-col>
                              <v-col cols="6" class="pa-0 pl-1">
                                <v-btn
                                  small
                                  block
                                  depressed
                                  class="rounded-lg text-none justify-start px-2"
                                  :disabled="!hasAvailableEmail(contact)"
                                  @click.stop.prevent="sendEmail(contact)"
                                  @mousedown.stop.prevent
                                >
                                  <v-icon small left>mdi-email</v-icon>
                                  <span class="text-caption font-weight-bold"
                                    >Email</span
                                  >
                                </v-btn>
                              </v-col>
                            </v-row>
                          </div>
                        </v-expand-transition>

                        <v-divider
                          v-if="i !== contacts.length - 1"
                          class="my-0"
                        ></v-divider>
                      </div>
                    </v-list>
                  </div>
                </div>
              </v-expand-transition>
            </v-card>
          </v-dialog>
        </span>

        <v-btn
          v-if="showOpenNewTab"
          icon
          small
          aria-label="Open account in new tab"
          @click.stop.prevent="openAccountInNewTab"
          @mousedown.stop.prevent
        >
          <v-icon small color="review">mdi-open-in-new</v-icon>
        </v-btn>

        <v-icon v-else color="grey"> mdi-chevron-right </v-icon>
      </div>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
// Libraries
import { mapGetters } from "vuex";

// Components
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";

export default {
  name: "AccountCell",
  components: {
    StatusChip,
  },
  data() {
    return {
      logoLoaded: false,
      logoLoading: false,
      logo: null,
      actionsMenuOpen: false,
      contactsExpanded: false,
      contactsLoading: false,
      contactsLoaded: false,
      contacts: [],
      accountMetaLoading: false,
      accountMetaLoaded: false,
      accountMeta: {
        // Match Account_View.vue: status comes from brandStats.status (brand-specific)
        brandStatus: null,
        accountStatus: null, // fallback only
        parent: null,
        phoneNumber: "",
        email: "",
        location: null,
      },
    };
  },
  props: {
    account: {
      type: Object,
      required: true,
    },
    fetchLogo: {
      type: Function,
      required: true,
    },
  },
  watch: {
    account: {
      handler(newAccount, oldAccount) {
        // Only re-fetch if the account ID actually changed
        if (newAccount?.objectID !== oldAccount?.objectID) {
          this.updateAccountLogo(newAccount.objectID);
          // Reset contacts cache when account changes
          this.contacts = [];
          this.contactsLoaded = false;
          this.contactsLoading = false;
          this.contactsExpanded = false;
          // Reset account meta cache
          this.accountMetaLoaded = false;
          this.accountMetaLoading = false;
          this.accountMeta = {
            brandStatus: null,
            accountStatus: null,
            parent: null,
            phoneNumber: "",
            email: "",
            location: null,
          };
          // Reset quick actions menu state
          this.actionsMenuOpen = false;
        }
      },
      immediate: false
    }
  },
  async mounted() {
    this.logoLoading = true;
    this.logo = await this.fetchLogo(this.account.objectID, this.$cacheManager);
    this.logoLoading = false;
  },
  methods: {
    async updateAccountLogo(accountID) {
      this.logoLoading = true;
      this.logo = null; // Clear the current logo
      this.logo = await this.fetchLogo(accountID, this.$cacheManager);
      this.logoLoading = false;
    },
    navigateToAccount() {
      // Close the menu before navigating
      this.closeMenus();
      this.$router.push({
        name: "accounts-view",
        params: {
          id: this.account.objectID,
        },
      });
    },
    openAccountInNewTab() {
      const id = this.account?.objectID;
      if (!id) return;
      const link = this.$router.resolve({
        name: "accounts-view",
        params: { id },
      });
      window.open(link.href, "_blank", "noopener,noreferrer");
    },
    openActionsMenu(event) {
      // Prevent navigation to account when clicking quick actions
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      // Always reset contacts expansion
      this.contactsExpanded = false;

      // Use Vue.set to ensure reactivity, especially on first click
      this.$set(this, "actionsMenuOpen", true);
    },
    closeMenus() {
      this.actionsMenuOpen = false;
      this.$emit("close");
    },
    async toggleContacts() {
      this.contactsExpanded = !this.contactsExpanded;
      if (this.contactsExpanded) {
        await this.ensureContactsLoaded();
      }
    },
    async ensureAccountMetaLoaded() {
      if (this.accountMetaLoaded || this.accountMetaLoading) return;
      const accountId = this.account?.objectID;
      if (!accountId) return;

      this.accountMetaLoading = true;
      try {
        // 1) Always fetch account doc for parent + fallback status
        let parent = null;
        let accountStatus = null;
        let phoneNumber = "";
        let email = "";
        let location = null;
        const accountDocSnap = await getDoc(
          doc(firestore, "accounts", accountId),
        );
        if (accountDocSnap.exists()) {
          const data = accountDocSnap.data() || {};
          parent = data.parent ?? null;
          accountStatus = data.status ?? null;
          phoneNumber = data.phoneNumber || "";
          email = data.email || "";
          location = data.location || null;
        }

        // 2) Match Account_View.vue: status comes from brandStats.status
        let brandStatus = null;
        if (this.defaultBrandId) {
          const brandStatsID = `${accountId}_${this.defaultBrandId}`;
          const brandStatsSnap = await getDoc(
            doc(firestore, "brandStats", brandStatsID),
          );
          if (brandStatsSnap.exists()) {
            brandStatus = brandStatsSnap.data()?.status ?? null;
          }
        }

        this.accountMeta = {
          brandStatus,
          accountStatus,
          parent,
          phoneNumber,
          email,
          location,
        };
      } catch (e) {
        // ignore; we'll just show no status
      } finally {
        this.accountMetaLoading = false;
        this.accountMetaLoaded = true;
      }
    },
    async ensureContactsLoaded() {
      if (this.contactsLoaded || this.contactsLoading) return;
      this.contactsLoading = true;
      this.contacts = [];

      const accountId = this.account?.objectID;
      if (!accountId) {
        this.contactsLoading = false;
        this.contactsLoaded = true;
        return;
      }

      try {
        // Ensure we know parent/status (used for parent contacts + status badge fallback)
        await this.ensureAccountMetaLoaded();
        const parentId = this.accountMeta?.parent || null;

        const contactsRef = collection(firestore, "contacts");
        const activeQ = where("active", "==", true);
        const accountQ = query(
          contactsRef,
          where("relatedID", "==", accountId),
          activeQ,
        );

        const [accountContactsSnap] = await Promise.all([getDocs(accountQ)]);

        const accountContacts = accountContactsSnap.empty
          ? []
          : accountContactsSnap.docs
              .filter((d) => d.data()?.title !== "customer")
              .map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  parent: false,
                  fullName: `${data?.name?.first ?? ""} ${
                    data?.name?.last ?? ""
                  }`.trim(),
                  phoneNumber: data?.phoneNumber || "",
                  email: data?.email || "",
                  location: data?.location || null,
                  expanded: false,
                };
              });

        let parentContacts = [];
        if (parentId) {
          const parentQ = query(
            contactsRef,
            where("relatedID", "==", parentId),
            activeQ,
          );
          const parentSnap = await getDocs(parentQ);
          parentContacts = parentSnap.empty
            ? []
            : parentSnap.docs
                .filter((d) => d.data()?.title !== "customer")
                .map((d) => {
                  const data = d.data();
                  return {
                    id: d.id,
                    parent: true,
                    fullName: `${data?.name?.first ?? ""} ${
                      data?.name?.last ?? ""
                    }`.trim(),
                    phoneNumber: data?.phoneNumber || "",
                    email: data?.email || "",
                    location: data?.location || null,
                    expanded: false,
                  };
                });
        }

        this.contacts = [...accountContacts, ...parentContacts].filter(
          (c) => c.fullName.length > 0,
        );
      } catch (e) {
        // fail silently; menu will show empty state
        this.contacts = [];
      } finally {
        this.contactsLoading = false;
        this.contactsLoaded = true;
      }
    },
    hasAvailablePhone(contact) {
      return this.getPhoneValue(contact).length > 0;
    },
    hasAvailableEmail(contact) {
      return this.getEmailValue(contact).length > 0;
    },
    getPhoneValue(contact) {
      if (contact?.phoneNumber?.length) return contact.phoneNumber;
      if (this.account?.phoneNumber?.length) return this.account.phoneNumber;
      if (this.accountMeta?.phoneNumber?.length) return this.accountMeta.phoneNumber;
      return "";
    },
    getEmailValue(contact) {
      if (contact?.email?.length) return contact.email;
      if (this.account?.email?.length) return this.account.email;
      if (this.accountMeta?.email?.length) return this.accountMeta.email;
      return "";
    },
    getLocationValue(contact) {
      if (contact?.location) return contact.location;
      if (this.account?.location) return this.account.location;
      if (this.accountMeta?.location) return this.accountMeta.location;
      return null;
    },
    async startCall(contact) {
      let localCall = false;
      if (!this.accountMetaLoaded && !this.accountMetaLoading) {
        await this.ensureAccountMetaLoaded();
      }

      const phoneNumber = this.getPhoneValue(contact);
      if (!phoneNumber.length) return;

      // Mirror Account_View.vue behavior (dialpad if enabled, else tel://)
      let dialpadActive = false;
      try {
        const configDoc = doc(firestore, "config", "config");
        const configResponse = await getDoc(configDoc);
        if (configResponse.exists) {
          dialpadActive = !!configResponse.data().dialpad;
        }
      } catch (e) {
        dialpadActive = false;
      }

      const outboundCall = httpsCallable(functions, "outboundCall");
      const location = this.getLocationValue(contact);
      const countryCode =
        location?.country &&
        this.getCountryCodes?.find(
          (option) => option.name === location.country,
        )?.countryCode
          ? this.getCountryCodes.find(
              (option) => option.name === location.country,
            ).countryCode
          : "1";

      try {
        if (dialpadActive) {
          await outboundCall({
            email: this.getUser?.email,
            userPhoneNumber: this.getUser?.phoneNumber,
            contactPhoneNumber: `+${countryCode}${phoneNumber}`,
          });
        } else {
          localCall = true;
          window.open(`tel://${countryCode}${phoneNumber}`);
        }
      } catch (error) {
        if (!localCall) {
          window.open(`tel://${countryCode}${phoneNumber}`);
        }
      } finally {
        this.closeMenus();
      }
    },
    async sendEmail(contact) {
      if (!this.accountMetaLoaded && !this.accountMetaLoading) {
        await this.ensureAccountMetaLoaded();
      }

      const email = this.getEmailValue(contact);
      if (email.length) window.open(`mailto:${email}`);
      this.closeMenus();
    },
    openDirections(contact) {
      const address = [
        `${contact?.["location.shipping.street.number"] ?? ""} ${
          contact?.["location.shipping.street.name"] ?? ""
        }`.trim(),
        contact?.["location.shipping.city.name"] ?? "",
        contact?.["location.shipping.state.name"] ?? "",
        contact?.["location.shipping.zipCode"] ?? "",
      ]
        .filter(Boolean)
        .join(", ");

      const query = encodeURIComponent(address || contact?.name || "");
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        "_blank",
      );
      this.closeMenus();
    },
    createNewOrder() {
      if (!this.defaultBrandId) return;
      this.closeMenus();
      this.$router.push({
        name: "salesOrder-new",
        params: {
          brandStatsID: `${this.account.objectID}_${this.defaultBrandId}`,
        },
      });
    },
    /* API */
    // GET
    // POST
    /* */
  },
  computed: {
    ...mapGetters([
      // Main
      "getAppName",
      "getUser",
    ]),
  },
};
</script>

<style scoped>
/* Mobile: closer to standard list body/caption scale + comfortable line-height */
.account-cell--mobile-typography >>> .v-list-item__title {
  line-height: 1.35 !important;
}

.account-cell--mobile-typography >>> .v-list-item__subtitle {
  line-height: 1.4 !important;
  margin-top: 2px !important;
}

.account-cell--mobile-typography >>> .v-list-item__subtitle + .v-list-item__subtitle {
  margin-top: 2px !important;
}
</style>
